import { motion } from "framer-motion";
import { Mic, Image, FileText, Reply, Smile, CheckCheck, Users, Play } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

const CAPS = ["Text", "Photos", "Documents", "Voice notes", "Replies", "Reactions", "Read receipts", "Presence", "Groups"];

export const MessagingFirst = () => (
  <section id="features" className="bg-diem-paper py-28 sm:py-36" data-testid="messaging-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="03" label="Messaging first" />
      <div className="mt-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal delay={0.1}>
            <SectionHeading>Messaging first.</SectionHeading>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-lg leading-relaxed text-diem-slate max-w-lg">
              No training. No complicated workflows. Just message.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {CAPS.map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-white border border-diem-line px-4 py-2 text-sm font-semibold text-diem-slate hover:border-diem-blue/40 hover:text-diem-blue transition-colors duration-300 cursor-default"
                >
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="mt-10 font-display text-2xl sm:text-3xl font-medium tracking-tight text-diem-ink max-w-md">
              If you can send a message, <span className="text-diem-blue">you can use Deyem.</span>
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="rounded-3xl bg-white border border-diem-line shadow-[0_24px_60px_-16px_rgba(15,23,42,0.15)] p-5 sm:p-6" data-testid="messaging-demo">
            <div className="flex items-center gap-3 pb-4 border-b border-diem-line/70">
              <div className="flex -space-x-2.5">
                <img className="w-9 h-9 rounded-full border-2 border-white object-cover" alt="Sarah" loading="lazy"
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc4Nzc2OTIzOXww&ixlib=rb-4.1.0&q=85&w=96" />
                <img className="w-9 h-9 rounded-full border-2 border-white object-cover" alt="James" loading="lazy"
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc4Nzc2OTIzOXww&ixlib=rb-4.1.0&q=85&w=96" />
              </div>
              <div>
                <p className="text-sm font-bold text-diem-ink flex items-center gap-1.5">
                  Oakfield Drive <Users className="w-3.5 h-3.5 text-diem-muted" />
                </p>
                <p className="text-xs text-diem-ok font-semibold">Sarah is online</p>
              </div>
            </div>

            <div className="pt-5 space-y-4">
              <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.35 }} className="max-w-[85%]">
                <div className="rounded-2xl rounded-bl-md overflow-hidden border border-diem-line">
                  <img className="w-full h-36 object-cover" alt="Finished living room" loading="lazy"
                    src="https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwcmVub3ZhdGlvbnxlbnwwfHx8fDE3ODc4MTkxOTV8MA&ixlib=rb-4.1.0&q=85&w=800" />
                </div>
                <p className="mt-1.5 text-[11px] text-diem-muted font-medium">Sarah · Living room, finished coat</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }} className="max-w-[85%] ml-auto">
                <div className="rounded-2xl rounded-br-md bg-gradient-to-r from-[#1E3A8A] to-[#38BDF8] text-white px-4 py-3 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                    <Play className="w-3.5 h-3.5 fill-white" />
                  </span>
                  <span className="flex items-end gap-[3px] h-5">
                    {[8, 14, 18, 10, 16, 20, 12, 7, 15, 18, 9, 13].map((h, i) => (
                      <motion.span key={i} className="w-[3px] rounded-full bg-white/80" style={{ height: h }}
                        animate={{ scaleY: [1, 1.5, 1] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.08 }} />
                    ))}
                  </span>
                  <span className="text-[11px] font-semibold opacity-80">0:19</span>
                </div>
                <p className="mt-1.5 text-[11px] text-diem-muted font-medium text-right flex items-center justify-end gap-1">
                  Voice note · Seen 14:02 <CheckCheck className="w-3.5 h-3.5 text-diem-blue" />
                </p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.65 }} className="max-w-[85%]">
                <div className="rounded-2xl rounded-bl-md bg-diem-mist px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-white border border-diem-line flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-diem-err" />
                    </span>
                    <div>
                      <p className="text-[13px] font-bold text-diem-ink">planning-notes.pdf</p>
                      <p className="text-[11px] text-diem-muted">2.4 MB · PDF</p>
                    </div>
                  </div>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white border border-diem-line px-2.5 py-1 text-[11px] font-bold text-diem-slate">
                    👍 2
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-diem-muted font-medium">
                    <Reply className="w-3 h-3" /> Reply
                  </span>
                </div>
              </motion.div>
            </div>

            <div className="mt-5 flex items-center gap-2 border-t border-diem-line/70 pt-4">
              <Smile className="w-5 h-5 text-diem-muted" />
              <div className="flex-1 rounded-full bg-diem-mist px-4 py-2.5 text-[13px] text-diem-muted font-medium">Message…</div>
              <Mic className="w-5 h-5 text-diem-muted" />
              <Image className="w-5 h-5 text-diem-muted" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
